/**
 * Typing-indicator hook — both sender and receiver in one.
 *
 *   const { notifyTyping, typingUserIds } = useTyping(conversationId)
 *
 *   <input onChange={() => notifyTyping()} />
 *   {typingUserIds.length > 0 && <span>… is typing</span>}
 *
 * Sender:
 *   - Leading-edge throttle at **3 s** so a continuously-typing user emits
 *     a `typing` channel push every ~3 s. That refreshes the receiver's
 *     5-s TTL in `store.markTyping` so the indicator doesn't flicker.
 *   - Server enforces the same 1/3 s rate limit; client throttle just
 *     saves channel chatter.
 *
 * Receiver:
 *   - Pulls the `Set<userId>` of currently-typing peers from the store.
 *   - The local user is already filtered at the subscription layer
 *     (`useMessengerSubscription` skips `userId === me.id`).
 *   - Blocked contacts are masked on read. The server drops their `typing`
 *     at the source and `markTyping` refuses to record one, but an indicator
 *     that was already on screen when the block landed would otherwise sit
 *     there for its full 5-s TTL — and a re-seed can't put it back if the
 *     mask is applied here. Presence hides blocked users the same way.
 *   - The 5-s auto-decay lives in the store from W4 — when no fresh
 *     events arrive, the set shrinks on its own.
 *
 * Falls back to no-op when auth isn't hydrated.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { usePresenceStore } from '@/features/presence/store/presence.store'
import { pushTypingToConversation } from '../services/messenger.socket'
import { useMessengerStore } from '../store/messenger.store'

const PUSH_INTERVAL_MS = 3_000

interface UseTyping {
  notifyTyping: () => void
  /** User ids currently typing in this conversation (self excluded). */
  typingUserIds: string[]
}

export function useTyping(conversationId: string | null | undefined): UseTyping {
  const me = useAuthStore((s) => s.user)
  const lastPushedRef = useRef<number>(0)

  // Reset throttle when the conversation switches.
  useEffect(() => {
    lastPushedRef.current = 0
  }, [conversationId])

  const notifyTyping = useCallback(() => {
    if (!conversationId || !me?.id) return
    const now = Date.now()
    if (now - lastPushedRef.current < PUSH_INTERVAL_MS) return
    lastPushedRef.current = now
    // Fire-and-forget — promise rejection just means we couldn't reach the
    // socket right now; next keystroke after the throttle will retry.
    pushTypingToConversation(me.id, conversationId, me.name).catch(() => { /* silent */ })
  }, [conversationId, me?.id, me?.name])

  // Receiver — shallow-compare the projected array so identical sets across
  // store mutations don't re-render the consumer.
  const blockedIds = usePresenceStore((s) => s.blockedIds)

  const typingUserIds = useMessengerStore(
    useShallow((s) => {
      if (!conversationId) return [] as string[]
      const set = s.typingByConv.get(conversationId)
      return set ? Array.from(set).sort() : []
    }),
  )

  // useShallow returns a fresh-but-shallow-equal array; the useMemo also
  // applies the block mask without re-running on every render.
  const stable = useMemo(
    () =>
      blockedIds.size === 0
        ? typingUserIds
        : typingUserIds.filter((id) => !blockedIds.has(id)),
    [typingUserIds, blockedIds],
  )

  return { notifyTyping, typingUserIds: stable }
}
